import type { Level } from '../core/hp/types';
import { itemFootprint } from '../core/geometry/footprint';
import { verticalExtent } from '../core/geometry/stacking';
import { alongWall, itemFacesWall } from '../state/wallPlan';
import type { SelectionTarget, WorldPoint } from './protocol';

/** Flat selection contours; wall elevations keep the real sill, height and wall depth, and an item its box on the face. */
export function selectionOutline(target: SelectionTarget, level: Level, wallView?: { wallId: string; side: 'front' | 'back' }): WorldPoint[] {
  const close = (points: WorldPoint[]) => points.length ? [...points, points[0]] : [];
  if (target.kind === 'item') {
    const item = level.items.find(item => item.id === target.id);
    if (!item) return [];
    const wall = wallView && level.walls.find(wall => wall.id === wallView.wallId);
    if (wallView) {
      if (!wall || !itemFacesWall(item, wall, wallView.side)) return [];
      const along = itemFootprint(item).map(corner => alongWall(wall, corner)), { bottom, top } = verticalExtent(item);
      const across = (wallView.side === 'front' ? 1 : -1) * wall.thickness / 2;
      const at = (t: number, y: number): WorldPoint => ({ x: wall.start.x + wall.dir.x * t - wall.dir.z * across, z: wall.start.z + wall.dir.z * t + wall.dir.x * across, y });
      const t0 = Math.min(...along), t1 = Math.max(...along);
      return close([at(t0, bottom), at(t1, bottom), at(t1, top), at(t0, top)]);
    }
    return close(itemFootprint(item).map(point => ({ ...point, y: item.pivot === 'center' ? item.elevation - item.size.h / 2 : item.elevation })));
  }
  if (target.kind === 'room') return close((level.rooms.find(room => room.index === target.index)?.polygon ?? []).map(point => ({ ...point, y: 0 })));
  const opening = target.kind === 'opening' ? level.openings.find(opening => opening.id === target.id) : undefined;
  const wall = level.walls.find(wall => wall.id === (target.kind === 'wall' ? target.id : opening?.wallId));
  if (!wall || wall.hidden || wallView && wallView.wallId !== wall.id) return [];
  const start = opening ? opening.offset - opening.width / 2 : 0;
  const end = opening ? opening.offset + opening.width / 2 : Math.hypot(wall.end.x - wall.start.x, wall.end.z - wall.start.z);
  const point = (along: number, across: number, y: number): WorldPoint => ({ x: wall.start.x + wall.dir.x * along - wall.dir.z * across,
    z: wall.start.z + wall.dir.z * along + wall.dir.x * across, y });
  const half = wall.thickness / 2;
  if (wallView) {
    const side = wallView.side === 'front' ? half : -half, bottom = opening?.sill ?? 0, top = bottom + (opening?.height ?? wall.height);
    return close([point(start, side, bottom), point(end, side, bottom), point(end, side, top), point(start, side, top)]);
  }
  return close([point(start, -half, 0), point(end, -half, 0), point(end, half, 0), point(start, half, 0)]);
}
